import { XmlCursorPos, XMLCursorPositions } from './xmlCursorPos.class';
import { XmlRules } from '../xml-rules-check/xmlRules.class';
import { XmlToolbox } from '../xmlToolbox.class';
import { DomDummyNodeManager } from '../dummyNodes/domDummyNodeManager.class';
import { Xml2html } from '../xml-html-converter/xml2html.class';

export class XmlCursorPosMover {
  public static moveLeft(pos: XmlCursorPos, rootNode: Element, xmlRules: XmlRules): boolean {
    if (pos === undefined) {
      return false;
    }

    switch (pos.PosAtNode) {
      case XMLCursorPositions.CursorOnCompleteNode:
        // Put in front of the node
        pos.SetCursor(pos.ActualNode, XMLCursorPositions.CursorInFrontOfNode);
        break;

      case XMLCursorPositions.CursorInFrontOfNode:
        if (pos.ActualNode === rootNode) {
          return false;
        } else {
          const previousSibling = DomDummyNodeManager.getPreviousSibling(pos.ActualNode);
          if (previousSibling) {
            // Previous sibling node exists
            pos.setCursorpBehindNodeForLeftMovement(previousSibling);
          } else {
            // no previous sibling node available -> set before parent node
            pos.SetCursor(pos.ActualNode.parentNode, XMLCursorPositions.CursorInFrontOfNode);
          }
        }
        break;

      case XMLCursorPositions.CursorBehindNode:
        pos.setCursorpBehindNodeForLeftMovement(pos.ActualNode);
        break;

      case XMLCursorPositions.CursorInEmptyNode:
        // Put in front of the node
        pos.SetCursor(pos.ActualNode, XMLCursorPositions.CursorInFrontOfNode);
        break;

      case XMLCursorPositions.CursorInsideTextNode:
        if (XmlToolbox.IsTextOrCommentNode(pos.ActualNode)) {
          // Node is Textnode
          if (pos.PosInTextnode > 1) {
            // Cursor one character to the left
            pos.SetCursor(pos.ActualNode, pos.PosAtNode, pos.PosInTextnode - 1);
          } else {
            // Put in front of the node
            pos.SetCursor(pos.ActualNode, XMLCursorPositions.CursorInFrontOfNode);
          }
        } else {
          // no Textnode
          const elemt = pos.ActualNode as Element;
          throw new Error(`XMLCursorPos.MoveLeft: CursorPos is XMLCursorPositionen.CursorInnerhalbDesTextNodes,
                           but no text node has been chosen, but the node ${elemt ? elemt.outerHTML : ''}`);
        }
        break;

      default:
        throw new Error(`XMLCursorPos.MoveLeft: unknown CursorPos ${pos.PosAtNode}`);
    }
    return true;
  }

  public static moveRight(pos: XmlCursorPos, rootNode: Element, xmlRules: XmlRules): boolean {
    if (pos === undefined) {
      return false;
    }

    switch (pos.PosAtNode) {
      case XMLCursorPositions.CursorOnCompleteNode:
        // Hinter den Node setzen
        const tagName = Xml2html.getTagNameFromNode(pos.ActualNode);
        const rule = xmlRules.ruleElements.get(tagName);
        if (rule && rule.empty === true) {
          pos.setCursorBehindNodeForDefaultOrRightMovement(pos.ActualNode);
        } else {
          const firstChild = DomDummyNodeManager.getFirstChild(pos.ActualNode);
          if (firstChild) {
            pos.SetCursor(firstChild, XMLCursorPositions.CursorInFrontOfNode);
          } else {
            pos.SetCursor(pos.ActualNode, XMLCursorPositions.CursorInEmptyNode);
          }
        }
        break;

      case XMLCursorPositions.CursorBehindNode:
        const nextSibling = DomDummyNodeManager.getNextSibling(pos.ActualNode);
        if (nextSibling) {
          // cursor behind node should only be set, when no other siblings are following
          const elemt = pos.ActualNode as Element;
          throw new Error(`XMLCursorPos.MoveRight: cursor behind node should only be set, when no other siblings are following ${elemt ? elemt.outerHTML : ''}`);
        } else {
          // No following siblings present, then set after parent node
          const parentNextSibling = DomDummyNodeManager.getNextSibling(pos.ActualNode.parentElement);
          if (parentNextSibling) {
            pos.SetCursor(parentNextSibling, XMLCursorPositions.CursorInFrontOfNode);
          } else {
            pos.SetCursor(pos.ActualNode.parentElement, XMLCursorPositions.CursorBehindNode);
          }
        }
        break;

      case XMLCursorPositions.CursorInEmptyNode:
        // Place behind the node
        pos.setCursorBehindNodeForDefaultOrRightMovement(pos.ActualNode);
        break;

      case XMLCursorPositions.CursorInFrontOfNode:
        if (XmlToolbox.IsTextOrCommentNode(pos.ActualNode)) {
          // Node is Textnode
          if ((pos.ActualNode as Element).textContent.length > 1) {
            // Textnode is not empty
            pos.SetCursor(pos.ActualNode, XMLCursorPositions.CursorInsideTextNode, 1); // one character forward, behind the first character
          } else {
            // Textnode is empty
            // put behind the node
            pos.SetCursor(pos.ActualNode, XMLCursorPositions.CursorBehindNode);
            // Because "CursorBehindNode" is not attractive, one more step
            this.moveRight(pos, rootNode, xmlRules);
          }
        } else {
          // Node is not Textnode
          const childNodeCount = DomDummyNodeManager.getChildNodeCount(pos.ActualNode);
          if (childNodeCount === 0) {
            // no Children existing
            if (Xml2html.isNoClosingElement(pos.ActualNode)) {
              // If no closed tag is displayed for this node, then directly behind the node
              // Place behind the node
              pos.setCursorBehindNodeForDefaultOrRightMovement(pos.ActualNode);
            } else {
              // Node has closing tag, so put in between
              // Place in empty node
              pos.SetCursor(pos.ActualNode, XMLCursorPositions.CursorInEmptyNode);
            }
          } else {
            // Children existing
            const firstChild = DomDummyNodeManager.getFirstChild(pos.ActualNode);
            pos.SetCursor(firstChild, XMLCursorPositions.CursorInFrontOfNode);
          }
        }
        break;

      case XMLCursorPositions.CursorInsideTextNode:
        if (XmlToolbox.IsTextOrCommentNode(pos.ActualNode)) {
          // Node is Textnode
          if ((pos.ActualNode as Element).textContent.length > pos.PosInTextnode + 1) {
            // there is still text in the right of the textnode
            // step one character forwards: behind the first char
            pos.SetCursor(pos.ActualNode, pos.PosAtNode, pos.PosInTextnode + 1);
          } else {
            // no text after the textnode
            // place cursor behind the node
            pos.setCursorBehindNodeForDefaultOrRightMovement(pos.ActualNode);
          }
        } else {
          // Node is no Textnode
          const elemt = pos.ActualNode as Element;
          throw new Error(`XMLCursorPos.MoveRight: CursorPos is XMLCursorPositionen.CursorInnerhalbDesTextNodes, 
                        but no text node has been chosen, but the node ${elemt ? elemt.outerHTML : ''}`);
        }
        break;
      default:
        throw new Error(`String.Format("XMLCursorPos.MoveRight: unknown CursorPos ${pos.PosAtNode}`);
    }

    return true;
  }
}
